/**
 * @file Forbidden.js — Page d'erreur 403 (accès interdit).
 * Affichée lorsqu'un utilisateur tente d'accéder à une ressource
 * pour laquelle il n'a pas les permissions nécessaires (R-0124).
 */
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../lib/routes';

/**
 * Page d'erreur 403 avec code d'erreur, message explicite et boutons contextuels.
 * Si l'utilisateur n'est pas connecté, propose de se connecter.
 * @returns {React.ReactElement}
 */
export default function Forbidden() {
  const { user } = useAuth();
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground px-4" data-testid="error-403-page">
      <ShieldAlert className="h-12 w-12 text-destructive mb-4" />
      <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('errors.403_title')}</h1>
      <p className="text-lg text-muted-foreground mb-1">{t('errors.403_heading')}</p>
      <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
        {user ? t('errors.403_message_auth') : t('errors.403_message_anon')}
      </p>
      <p className="text-xs text-muted-foreground mb-6">
        {t('errors.error_code_label')}: <code className="px-1 py-0.5 bg-muted rounded">{t('errors.403_code')}</code>
      </p>
      <div className="flex gap-3">
        {user ? (
          <Button asChild>
            <Link to={APP_ROUTES.dashboard}>{t('errors.go_dashboard')}</Link>
          </Button>
        ) : (
          <>
            <Button asChild>
              <Link to={APP_ROUTES.login}>{t('errors.go_login')}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={APP_ROUTES.home}>{t('errors.go_home')}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
